import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { originPathPrefix } from './faro-telemetry';
import { getSessionName } from './session-name';

export const SESSION_ID_HEADER = 'X-Session-Id';

// SignalR is not routed through Angular's HttpClient (its own client makes the negotiate call
// directly), so only the API prefix is relevant here - unlike faro-telemetry.ts's tracing
// ignoreUrls, which has to catch every fetch/XHR regardless of library.
const API_URL_PATTERN = new RegExp(`^${originPathPrefix(new URL(environment.apiUrl, window.location.href))}`);

/**
 * Attaches the server-minted session name (see session-name.ts) to every request to this app's
 * own API, so the many separate traces one session produces (each request keeps its own trace by
 * design) can be found as a group by session.id in Tempo/Loki. A no-op when the request is not to
 * the API. Waits for the one-time session-name fetch to resolve before forwarding a matching
 * request rather than racing it - the fetch is cached, so this only adds latency to the very
 * first API call the app makes.
 */
@Injectable()
export class SessionCorrelationInterceptor implements HttpInterceptor {
    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (!API_URL_PATTERN.test(req.url)) {
            return next.handle(req);
        }
        return from(getSessionName()).pipe(
            switchMap(sessionName => {
                const toSend = sessionName ? req.clone({ setHeaders: { [SESSION_ID_HEADER]: sessionName } }) : req;
                return next.handle(toSend);
            }),
        );
    }
}
